import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useMemo, useRef } from 'react';
import { Calendar, dayjsLocalizer, ToolbarProps } from 'react-big-calendar';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import EventStatusBadge from '../../ui/EventStatusBadge';
import { getDateForCalendar, getHoursBetweenDates } from '../../../utils/dates';
import { COMMON_TRANSLATE_KEYS } from '../../../constants/translate-keys';

import { useResponsiveWindow } from '../../../hooks/useResponsiveWindow';
import { useAuth } from '../../../hooks/useAuth';
import { getObjectTranslation } from '../../../utils/language';
import { Currency, FieldPrice, Game } from '../../../types';
import { getBookingDateTimePrice } from '../../../utils/getHelpers';
import { useNavigate } from 'react-router-dom';

import 'dayjs/locale/en';
import 'dayjs/locale/bg';

import 'react-big-calendar/lib/css/react-big-calendar.css';
import './index.css';

interface GameReservation {
  start: Date;
  end: Date;
  status: 'CONFIRMED' | 'DECLINED' | 'PENDING' | 'FREE' | 'PASSED';
  game?: Game;
  onShowDetailsClick?: (game: Game) => void;
  price: number;
  currencyData: Currency;
  fieldId: number;
  complexId: string;
}

interface EventCalendarProps {
  complexName: string | undefined;
  fieldName: string | undefined;
  games: Game[];
  fieldId: number;
  fieldPrice: number;
  fieldPreferentialPrices: FieldPrice[];
  fieldPriceCurrency: Currency;
  complexId: string;
  onShowDetailsClick?: (game: Game) => void;
  startDate: Date;
  endDate: Date;
  setStartDate: (date: Date) => void;
  setEndDate: (date: Date) => void;
  openingHours: string;
  closingHours: string;
}

const CustomEvent = ({ event }: { event: GameReservation }) => {
  const { user } = useAuth();

  const { t, i18n } = useTranslation();

  const navigate = useNavigate();

  const {
    additionalTextClassName,
    isPendingOrConfirmed,
    borderColor,
    cursorPointer,
  } = useMemo(() => {
    let borderColor: string = '';
    let cursorPointer: string = 'cursor-pointer';
    let textColor = '';

    const { status } = event;

    switch (status) {
      case 'CONFIRMED':
        borderColor = 'border-functionalRed';
        textColor = 'text-functionalRed';
        break;
      case 'PENDING':
        borderColor = event.game?.isExpired
          ? 'border-additionalGray'
          : 'border-mainBlue';
        textColor = event.game?.isExpired
          ? 'text-additionalGray'
          : 'text-mainBlue';
        break;
      case 'FREE':
        borderColor = 'border-mainGreen';
        textColor = 'text-mainGreen';
        break;
      default:
        borderColor = 'border-backGroundGray';
        textColor = 'text-backGroundGray';
        cursorPointer = '';
    }

    const isPendingOrConfirmed = status === 'PENDING' || status === 'CONFIRMED';
    const additionalTextClassName = `text-sm text-center ${textColor} whitespace-nowrap text-ellipsis overflow-hidden w-[99%]`;

    return {
      isPendingOrConfirmed,
      additionalTextClassName,
      borderColor,
      cursorPointer,
    };
  }, [event]);

  const handleClick = useCallback(() => {
    if (event.game && event.onShowDetailsClick) {
      return event.onShowDetailsClick(event.game);
    }

    if (event.status === 'FREE') {
      return navigate(
        `/book-field/${event.complexId}/${event.fieldId}?d=${dayjs(
          event.start
        ).toISOString()}&s=${dayjs(event.start).toISOString()}&e=${dayjs(
          event.end
        ).toISOString()}`
      );
    }
  }, [event, navigate]);

  return (
    <div
      className={`flex flex-col justify-center items-center h-full rounded border bg-additionalBlack gap-1 ${cursorPointer} ${borderColor}`}
      onClick={handleClick}
    >
      {event.status !== 'PASSED' && (
        <EventStatusBadge
          status={event.status}
          className="gap-1 w-[90%]"
          style={{ margin: 0 }}
          confirmedStatusKey={COMMON_TRANSLATE_KEYS.TAKEN}
          isExpired={event.game?.isExpired}
        />
      )}
      {isPendingOrConfirmed && (
        <>
          {event?.game?.reservation!.team && (
            <p className={additionalTextClassName}>
              {event.game.reservation!.team.name}
            </p>
          )}
          <p className={additionalTextClassName}>
            {event.game?.reservation?.otherName ||
              `${event.game?.reservation?.creator.name}${
                event.game?.reservation?.creator.id === user?.id
                  ? ` (${t(COMMON_TRANSLATE_KEYS.YOU)})`
                  : ''
              }`}
          </p>
        </>
      )}
      {event.status === 'FREE' && (
        <p className={additionalTextClassName}>
          {event.price.toFixed(2)}{' '}
          {getObjectTranslation(event.currencyData, i18n.language)}
        </p>
      )}
    </div>
  );
};

const CustomToolbar = ({
  localizer,
  currentDate,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
}: {
  localizer: any;
  currentDate: Date;
  startDate: Date;
  endDate: Date;
  setStartDate: (date: Date) => void;
  setEndDate: (date: Date) => void;
}) => {
  const handleNextWeek = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      setStartDate(dayjs(startDate).add(1, 'w').toDate());
      setEndDate(dayjs(endDate).add(1, 'w').toDate());
    },
    [startDate, endDate, setStartDate, setEndDate]
  );

  const handlePrevWeek = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      setStartDate(dayjs(startDate).subtract(1, 'w').toDate());
      setEndDate(dayjs(endDate).subtract(1, 'w').toDate());
    },
    [startDate, endDate, setStartDate, setEndDate]
  );

  return (
    <div className="py-2">
      <div className="flex items-center justify-center">
        <button onClick={handlePrevWeek}>
          <ChevronLeftIcon className="w-5 h-5 text-white" />
        </button>
        <label className="cursor-pointer pb-[1px]">
          {dayjs(startDate).format('DD MMMM')} -{' '}
          {dayjs(endDate).format('DD MMMM')}
        </label>
        <button onClick={handleNextWeek}>
          <ChevronRightIcon className="w-5 h-5 text-white" />
        </button>
      </div>
    </div>
  );
};

const EventCalendar: React.FC<EventCalendarProps> = ({
  games,
  fieldPrice,
  fieldPriceCurrency,
  fieldPreferentialPrices,
  onShowDetailsClick,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  openingHours,
  closingHours,
  complexId,
  fieldId,
}) => {
  const { i18n } = useTranslation();

  const { isTablet } = useResponsiveWindow();

  const containerRef = useRef<HTMLDivElement>(null);

  const localizer = useMemo(() => {
    dayjs.locale(i18n.language);
    return dayjsLocalizer(dayjs);
  }, [i18n.language]);

  const formats = useMemo(
    () => ({
      timeGutterFormat: (date: Date, culture: any, localizer: any) => {
        return `${localizer.format(
          date,
          'HH:mm',
          culture
        )} - ${localizer.format(dayjs(date).add(1, 'hour'), 'HH:mm', culture)}`;
      },
      dayFormat: (date: Date, culture: any, localizer: any) => {
        if (isTablet) {
          return dayjs(date).format('ddd \n DD.MM');
        }
        return dayjs(date).format('dddd \n DD.MM.YYYY');
      },
    }),
    [isTablet]
  );

  const events: GameReservation[] = useMemo<GameReservation[]>(() => {
    const [startHour] = openingHours.split(':');
    const [endHour] = closingHours.split(':');

    const hoursInCurrentWeek = getHoursBetweenDates(
      startDate,
      endDate,
      Number(startHour),
      Number(endHour)
    );

    const now = dayjs().toDate();

    const emptyEvents: GameReservation[] = hoursInCurrentWeek.reduce<
      GameReservation[]
    >((current, next) => {
      if (current.find((x) => x.start === next || x.end === next)) {
        return current;
      }

      if (next.getHours() === Number(endHour)) {
        return current;
      }

      const price = getBookingDateTimePrice(
        next,
        fieldPreferentialPrices,
        fieldPrice
      );

      if (next < now) {
        current.push({
          start: next,
          end: dayjs(next).add(1, 'h').toDate(),
          status: 'PASSED',
          price,
          currencyData: fieldPriceCurrency,
          complexId,
          fieldId,
        });
        return current;
      }

      current.push({
        start: next,
        end: dayjs(next).add(1, 'h').toDate(),
        status: 'FREE',
        price,
        currencyData: fieldPriceCurrency,
        complexId,
        fieldId,
      });

      return current;
    }, []);

    const mapped = games.map((game) => {
      return {
        start: getDateForCalendar(game.startWithDate),
        end: getDateForCalendar(game.endWithDate),
        status: game.status,
        game: game,
        onShowDetailsClick: onShowDetailsClick
          ? () => onShowDetailsClick(game)
          : undefined,
        price: fieldPrice,
        currencyData: fieldPriceCurrency,
      };
    });

    const allEvents = emptyEvents.map((e) => {
      const reservation = mapped.find(
        (x) =>
          (x.start.toISOString() === e.start.toISOString() ||
            x.end.toISOString() === e.end.toISOString()) &&
          ['CONFIRMED', 'PENDING'].includes(x.status)
      );

      if (reservation) {
        return {
          ...reservation,
          complexId,
          fieldId,
        };
      }

      return {
        ...e,
        complexId,
        fieldId,
      };
    });

    return allEvents.reduce<GameReservation[]>((prev, next, i, arr) => {
      const sameDateEventIndex = prev.findIndex(
        (x) =>
          dayjs(x.start).isSame(dayjs(next.start), 'date') &&
          dayjs(x.start).isSame(dayjs(next.start), 'hour')
      );

      if (sameDateEventIndex === -1) {
        prev.push(next);
      } else {
        const sameDateElements = arr.filter((x) =>
          dayjs(x.start).isSame(next.start)
        );

        prev[sameDateEventIndex] =
          sameDateElements.find(
            (x) => x.status === 'CONFIRMED' || x.status === 'PENDING'
          ) || sameDateElements[sameDateElements.length - 1];
      }

      return prev;
    }, []);
  }, [
    openingHours,
    closingHours,
    startDate,
    endDate,
    games,
    fieldPreferentialPrices,
    fieldPrice,
    fieldPriceCurrency,
    onShowDetailsClick,
    complexId,
    fieldId,
  ]);

  const earliestHour = useMemo(() => {
    const [hours, minutes] = openingHours.split(':');

    const dateWithOpeningHours = new Date(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate(),
      +hours,
      +minutes
    );

    return dateWithOpeningHours;
  }, [openingHours, startDate]);

  const latestHour = useMemo(() => {
    const [hours, minutes] = closingHours.split(':');

    const dateWithClosingHours = new Date(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate(),
      +hours,
      +minutes
    );

    return dateWithClosingHours;
  }, [closingHours, startDate]);

  const renderToolbar = useCallback(
    (calendarProps: ToolbarProps<GameReservation, object>) => {
      return (
        <CustomToolbar
          localizer={localizer}
          currentDate={startDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          startDate={startDate}
          endDate={endDate}
        />
      );
    },
    [endDate, localizer, setEndDate, setStartDate, startDate]
  );

  return (
    <div
      ref={containerRef}
      className="p-4 bg-mainBlack text-mainWhite shadow-lg rounded-lg h-full min-w-[600px]"
    >
      <Calendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        style={{ height: '100%' }}
        views={['week']}
        defaultView="week"
        date={startDate}
        min={earliestHour}
        max={latestHour}
        formats={formats}
        components={{
          event: CustomEvent,
          toolbar: renderToolbar,
        }}
      />
    </div>
  );
};

export default EventCalendar;
